import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { MapPin, Phone, Mail, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function ContactSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: "", company: "", email: "", phone: "", message: "",
  });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Name is required";
    if (!formData.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = "Invalid email address";
    if (!formData.message.trim()) errs.message = "Message is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus("success");
      setFormData({ name: "", company: "", email: "", phone: "", message: "" });
      setErrors({});
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-white w-full"
    >
      <div className="flex justify-center md:flex-row flex-col max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1] mb-4">
            Get in Touch
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.2] font-semibold text-[#0C3A72] mb-4">
            Let's Discuss Your Requirements
          </motion.h2>
          <motion.div variants={fadeUp} className="section-divider mx-auto" />
        </motion.div>

        <div className="flex justify-center max-w-2xl gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-2 space-y-8"
          >
            <div>
              <h3 className="text-2xl font-medium text-[#0C3A72] mb-6">Contact Information</h3>
              <p className="text-base text-[#4A627E] leading-relaxed mb-8">
                Ready to explore indigenous aviation chemistry solutions? Our team is here to assist with technical specifications, pricing, and trial arrangements.
              </p>
            </div>

            <div className="space-y-6">
              <ContactInfo icon={User} label="Contact Person" value="Satyam Purohit" />
              <ContactInfo icon={MapPin} label="Address" value="No. 54, DV Nagar, Bengaluru, Karnataka 560016" />
              <ContactInfo icon={Phone} label="Mobile" value="+91 78291 17300" href="tel:+917829117300" />
              <ContactInfo icon={Mail} label="Email" value="satyam.purohit@odofree.com" href="mailto:satyam.purohit@odofree.com" />
            </div>

            <div className="border-t border-[#0C3A72]/10 pt-6">
              <p className="text-sm text-[#4A627E]">
                <span className="text-xl font-semibold text-[#0C3A72]">OZO AeroCare</span> 
              </p>
              <p className="text-sm text-[#4A627E] mt-1">Proudly Developed & Made in India 🇮🇳</p>
            </div>
          </motion.div>

          {/* Contact Form */}
          {/* <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:col-span-3"
          >
            <form onSubmit={handleSubmit} noValidate data-testid="contact-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[#0C3A72] mb-2 block">
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange("name")}
                    className={`bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72] placeholder:text-[#4A627E]/50 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="company" className="text-sm font-medium text-[#0C3A72] mb-2 block">Company</Label>
                  <Input
                    id="company"
                    data-testid="contact-company-input"
                    placeholder="Company name"
                    value={formData.company}
                    onChange={handleChange("company")}
                    className="bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72] placeholder:text-[#4A627E]/50"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#0C3A72] mb-2 block">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange("email")}
                    className={`bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72] placeholder:text-[#4A627E]/50 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-[#0C3A72] mb-2 block">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.phone}
                    onChange={handleChange("phone")}
                    className="bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72] placeholder:text-[#4A627E]/50"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message" className="text-sm font-medium text-[#0C3A72] mb-2 block">
                  Message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  placeholder="Tell us about your requirements..."
                  value={formData.message}
                  onChange={handleChange("message")}
                  rows={5}
                  className={`bg-[#F7F4EC] border-[#0C3A72]/15 focus:ring-2 focus:ring-[#0077D1]/50 focus:border-[#0077D1] text-[#0C3A72] placeholder:text-[#4A627E]/50 resize-none ${errors.message ? 'border-red-500' : ''}`}
                />
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={submitting}
                className="w-full bg-[#0077D1] text-white py-3.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#005BA1] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-success-message"
                  className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm text-center"
                >
                  Thank you for reaching out. Our team will respond within 24 hours.
                </motion.div>
              )}
              {status === "error" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-error-message"
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm text-center"
                >
                  Something went wrong. Please try again or contact us directly.
                </motion.div>
              )}
            </form>
          </motion.div> */}
        </div>
      </div>
    </section>
  );
}

function ContactInfo({ icon: Icon, label, value, href }) {
  const content = href ? (
    <a href={href} className="text-[#0C3A72] hover:text-[#0077D1] transition-colors">{value}</a>
  ) : (
    <span className="text-[#0C3A72]">{value}</span>
  );
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-[#0077D1]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#0077D1]" />
      </div>
      <div>
        <p className="text-sm text-[#4A627E] mb-0.5">{label}</p>
        <p className="text-base font-medium">{content}</p>
      </div>
    </div>
  );
}
